import { computeCbamLiabilityEuro } from '@/shared/utils/computeCbamLiabilityEuro'
import {
  computeLiabilityLowerBound,
  computeLiabilityUpperBound
} from '@/shared/utils/computeCbamLiabilityBounds'
import type { AggregatedProjection } from '@/features/cbam-reporting/hooks/cbamLiability'
import type { CbamModifiers } from '@/shared/types/cbamModifiers'
import { QUARTERS, type Quarter } from '@/config/cbamReportingPeriods'

type SimulationYearTotals = {
  totalVolume: number
  totalDirectEmissions: number
  totalCertificates: number
  totalLiabilityBase: number
  /** Certificates for high-CSCF scenario (when user CSCF < 100%); same as base when not used */
  totalCertificatesHigh: number
  /** Liability for lower-impact scenario (base × (1 - low%)) per quarter summed */
  totalLiabilityLow: number
  /** Liability for higher-impact scenario (high base × (1 + high%)) per quarter summed */
  totalLiabilityHigh: number
}

type LiabilityDataInput = {
  aggregatedProjection?: Record<string, AggregatedProjection>
  aggregatedProjectionByQuarter?: Record<string, Record<string, AggregatedProjection>>
}

/**
 * Computes year-level summary totals for the simulation details page.
 * Base and Low use 100% CSCF data; High uses optional high-CSCF data when user CSCF < 100%.
 * Reuses the same quarter iteration and modifier logic as CertificatesAndLiabilityPerQuarter.
 */
export function getSimulationYearTotals(
  liabilityDataBaseLow: LiabilityDataInput | undefined,
  modifiers: CbamModifiers | undefined,
  simulationYear: number,
  quartersWithGoods: Quarter[],
  liabilityDataHigh?: LiabilityDataInput | undefined
): SimulationYearTotals {
  const yearKey = String(simulationYear)
  const aggregatedBase = liabilityDataBaseLow?.aggregatedProjection?.[yearKey]
  const quartersByYearBase = liabilityDataBaseLow?.aggregatedProjectionByQuarter?.[yearKey]
  const aggregatedHigh = liabilityDataHigh?.aggregatedProjection?.[yearKey]
  const quartersByYearHigh = liabilityDataHigh?.aggregatedProjectionByQuarter?.[yearKey]

  const totalVolume = aggregatedBase?.volume ?? 0
  const totalDirectEmissions = aggregatedBase?.directEmissions ?? 0
  const totalCertificatesBase = aggregatedBase?.netEmissions ?? 0
  const totalCertificatesHigh = aggregatedHigh?.netEmissions ?? totalCertificatesBase

  const quartersToSum = [...quartersWithGoods].sort(
    (left, right) => QUARTERS.indexOf(left) - QUARTERS.indexOf(right)
  )

  let totalLiabilityBase = 0
  let totalLiabilityLow = 0
  let totalLiabilityHigh = 0

  for (const quarter of quartersToSum) {
    const projectionBase = quartersByYearBase?.[quarter]
    const projectionHigh = quartersByYearHigh?.[quarter]
    const quarterModifier = modifiers?.euaPriceQuarters?.[quarter]
    const price =
      quarterModifier?.value != null && quarterModifier.value !== ''
        ? parseFloat(quarterModifier.value)
        : 0
    const lowPct =
      quarterModifier?.lowPercentage != null && quarterModifier.lowPercentage !== ''
        ? parseFloat(quarterModifier.lowPercentage) / 100
        : 0
    const highPct =
      quarterModifier?.highPercentage != null && quarterModifier.highPercentage !== ''
        ? parseFloat(quarterModifier.highPercentage) / 100
        : 0

    const netEmissionsBase = projectionBase?.netEmissions ?? 0
    const baseLiability = price > 0 ? computeCbamLiabilityEuro(netEmissionsBase, price) : 0
    totalLiabilityBase += baseLiability
    totalLiabilityLow += computeLiabilityLowerBound(baseLiability, lowPct)

    const netEmissionsHigh = projectionHigh?.netEmissions ?? netEmissionsBase
    const highBaseLiability = price > 0 ? computeCbamLiabilityEuro(netEmissionsHigh, price) : 0
    totalLiabilityHigh += computeLiabilityUpperBound(highBaseLiability, highPct)
  }

  return {
    totalVolume,
    totalDirectEmissions,
    totalCertificates: totalCertificatesBase,
    totalLiabilityBase,
    totalCertificatesHigh,
    totalLiabilityLow,
    totalLiabilityHigh
  }
}
